import { morphSection } from '@theme/section-renderer';

/**
 * Colour is a separate product per colourway on this catalogue, so the swatch
 * row is a row of links to sibling products and choosing a colour is a
 * navigation. That is the correct information architecture and the wrong
 * interaction: every swatch cost a full document load, three seconds of network
 * and a scroll back to where the shopper already was.
 *
 * This keeps the links exactly as they are — they still work with JavaScript
 * off, in a new tab, and for a crawler — and upgrades the click into a section
 * morph. The sibling's page is fetched on intent (hover, focus, or the moment a
 * finger lands) so by the time the click arrives it is usually already in hand,
 * and its first gallery image is warmed at the same time so the swap does not
 * flash an empty frame.
 *
 * Every failure path is the same one: let the browser do the navigation it was
 * always going to do.
 */

/** @type {Map<string, Promise<string>>} */
const pages = new Map();

const PREFETCH_LIMIT = 12;

/**
 * @param {string} url
 * @returns {Promise<string>}
 */
function fetchPage(url) {
  let pending = pages.get(url);
  if (pending) return pending;

  if (pages.size >= PREFETCH_LIMIT) {
    const oldest = pages.keys().next().value;
    if (oldest) pages.delete(oldest);
  }

  pending = fetch(url, { headers: { 'X-Requested-With': 'lea-colourway' } }).then((response) => {
    if (!response.ok) throw new Error(`${response.status} for ${url}`);
    return response.text();
  });

  pending.catch(() => pages.delete(url));
  pages.set(url, pending);
  return pending;
}

/**
 * Pull the first gallery image of the fetched page into the image cache, so the
 * morph swaps to a picture that is already decoded rather than to a grey box.
 * @param {Document} doc
 */
function warmHeroImage(doc) {
  const img = doc.querySelector('media-gallery img, .product-media-container img');
  if (!(img instanceof HTMLImageElement)) return;

  const warm = new Image();
  if (img.sizes) warm.sizes = img.sizes;
  if (img.srcset) warm.srcset = img.srcset;
  if (img.src) warm.src = img.src;
}

class LeaColourway extends HTMLElement {
  /** @type {AbortController | null} */
  #activeSwap = null;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('pointerenter', this.#onIntent, { capture: true, passive: true });
    this.addEventListener('focusin', this.#onIntent, { passive: true });
    this.addEventListener('touchstart', this.#onIntent, { capture: true, passive: true });
    window.addEventListener('popstate', this.#onPopState);
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.#onPopState);
  }

  /**
   * @param {Event} event
   * @returns {HTMLAnchorElement | null}
   */
  #linkFrom(event) {
    const target = event.target;
    if (!(target instanceof Element)) return null;
    const link = target.closest('a.lea-swatches__item');
    if (!(link instanceof HTMLAnchorElement)) return null;
    if (link.getAttribute('aria-current') === 'page') return null;
    return link;
  }

  #onIntent = (/** @type {Event} */ event) => {
    const link = this.#linkFrom(event);
    if (!link) return;

    fetchPage(link.href)
      .then((html) => warmHeroImage(new DOMParser().parseFromString(html, 'text/html')))
      .catch(() => {});
  };

  #onClick = (/** @type {MouseEvent} */ event) => {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const link = this.#linkFrom(event);
    if (!link || link.target === '_blank') return;
    if (new URL(link.href).origin !== window.location.origin) return;

    event.preventDefault();
    this.#swap(link.href, true);
  };

  #onPopState = () => {
    // Only reclaim a step we took ourselves; anything else is a real navigation.
    if (!pages.has(window.location.href)) return;
    this.#swap(window.location.href, false);
  };

  /**
   * @param {string} url
   * @param {boolean} push
   */
  async #swap(url, push) {
    this.#activeSwap?.abort();
    const controller = new AbortController();
    this.#activeSwap = controller;

    const main = document.querySelector('main');
    main?.setAttribute('aria-busy', 'true');

    try {
      const html = await fetchPage(url);
      if (controller.signal.aborted) return;

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const ids = [];

      for (const section of doc.querySelectorAll('main .shopify-section[id^="shopify-section-"]')) {
        const id = section.id.replace('shopify-section-', '');
        if (document.getElementById(section.id)) ids.push(id);
      }

      // A sibling on a different template shares no section ids with this page,
      // and a morph of nothing would leave the shopper on the wrong product.
      if (!ids.length) throw new Error('no shared sections');

      for (const id of ids) morphSection(id, html);

      if (push) history.pushState({ leaColourway: true }, '', url);

      const title = doc.querySelector('title')?.textContent;
      if (title) document.title = title;

      const canonical = doc.querySelector('link[rel="canonical"]')?.getAttribute('href');
      const liveCanonical = document.querySelector('link[rel="canonical"]');
      if (canonical && liveCanonical) liveCanonical.setAttribute('href', canonical);

      document.dispatchEvent(new CustomEvent('lea:colourway:changed', { detail: { url } }));
    } catch (error) {
      if (controller.signal.aborted) return;
      window.location.href = url;
      return;
    } finally {
      if (this.#activeSwap === controller) {
        this.#activeSwap = null;
        main?.removeAttribute('aria-busy');
      }
    }
  }
}

if (!customElements.get('lea-colourway')) {
  customElements.define('lea-colourway', LeaColourway);
}
